import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Clock, TrendingUp } from "lucide-react";
import { LeadForm } from "../LeadForm";
import { GovBackedBanner } from "../leadForm/GovBackedBanner";
import { fadeUp } from "./shared";

const HERO_BG =
  "https://images.unsplash.com/photo-1549483249-f0b359d1e289?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxsb25kb24lMjBjaXR5JTIwYnVzaW5lc3MlMjBza3lsaW5lfGVufDB8fHx8MTc4NjYxMTg4OHww&ixlib=rb-4.1.0&q=85";

const TRUST_BADGES = [
  { icon: ShieldCheck, t: "No credit impact" },
  { icon: Clock, t: "2-minute check" },
  { icon: TrendingUp, t: "93% approval rate" },
];

const LINE_HIDDEN = { y: "110%" };
const LINE_SHOWN = { y: 0 };
const badgeInitial = { opacity: 0 };
const badgeAnimate = { opacity: 1 };
const badgeTransition = { delay: 0.2 };
const fundFasterTransition = { duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] };
const formWrapperInitial = { opacity: 0, y: 60 };
const formWrapperAnimate = { opacity: 1, y: 0 };
const formWrapperTransition = { duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] };

const HeroLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={LINE_HIDDEN}
      animate={LINE_SHOWN}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const HeroSection = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={heroRef} id="apply" className="relative min-h-[100vh] pt-32 pb-16 grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={HERO_BG} alt="London skyline" className="w-full h-[120%] object-cover opacity-[0.12] grayscale" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F6F5F2]/40 via-transparent to-[#F6F5F2]" />
      </motion.div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <motion.div
              initial={badgeInitial}
              animate={badgeAnimate}
              transition={badgeTransition}
              className="inline-flex items-center gap-2 rounded-full border border-[#052F24]/15 bg-white/50 px-4 py-1.5 text-xs font-bold tracking-[0.15em] uppercase mb-7"
            >
              <span className="w-2 h-2 rounded-full bg-[#052F24] animate-pulse" />
              UK business funding · FCA-regulated lenders
            </motion.div>

            <h1 className="font-display font-extrabold tracking-tighter leading-[0.9] text-5xl sm:text-6xl lg:text-[5.3rem]">
              <HeroLine delay={0.15}>Stop guessing.</HeroLine>
              <HeroLine delay={0.3}>
                Know your <span className="italic">options.</span>
              </HeroLine>
              <span className="block overflow-hidden">
                <motion.span
                  className="inline-block bg-[#052F24] text-[#DFFF00] px-3 -rotate-1"
                  initial={LINE_HIDDEN}
                  animate={LINE_SHOWN}
                  transition={fundFasterTransition}
                >
                  Fund faster.
                </motion.span>
              </span>
            </h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={7}
              className="mt-8 max-w-md text-lg font-light text-[#052F24]/70"
            >
              See what your business qualifies for instantly — matched to the single best UK lender. Funding paid out in as little as 6 hours.
            </motion.p>

            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={9} className="mt-8 flex flex-wrap gap-6">
              {TRUST_BADGES.map(({ icon: I, t }) => (
                <div key={t} className="flex items-center gap-2 text-sm font-medium">
                  <I size={18} className="text-[#052F24]" /> {t}
                </div>
              ))}
            </motion.div>

            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={11} className="mt-8 max-w-md">
              <GovBackedBanner />
            </motion.div>
          </motion.div>

          <motion.div
            initial={formWrapperInitial}
            animate={formWrapperAnimate}
            transition={formWrapperTransition}
            className="lg:col-span-5"
          >
            <LeadForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
